/* eslint-disable @typescript-eslint/camelcase */
// @file Stores that handles Plans and Upgrade modals
import { normalizedPaymentSchedule, SwitchType } from 'bits/billing_helper'
import { ALERT_ICON, THINKING_FACE_ICON } from 'bits/confirmation_dialog'
import { COLD_FACE_EMOJI, FACE_WITH_ROLLING_EYES_EMOJI } from 'bits/emoji'
import { captureMessage } from 'bits/error_tracker'
import { translatePrice, __ } from 'bits/intl'
import { currentUrl, navigateTo, reload, transformCurrentUrl } from 'bits/location'
import { poll } from 'bits/polling'
import { Billing as BillingApi, Plan as PlanApi } from 'dashboard/padlet_api'
import { AdvertisedQuotasResult, EstimateSwitchingCostResult, Plan, User } from 'types'
import { Module } from 'vuex'
import { ConfirmationDialogLayoutVariant } from 'vuexstore/modules/global_confirmation_dialog'

enum UpgradeStep {
  BelowPadletQuota = 'BelowPadletQuota',
  AtPadletQuota = 'AtPadletQuota',
  OverPadletQuota = 'OverPadletQuota',
  OverFileSizeQuota = 'OverFileSizeQuota',
  SchedulePro = 'SchedulePro',
  ConfirmProMonthly = 'ConfirmProMonthly',
  ConfirmProAnnual = 'ConfirmProAnnual',
  // New values for Gold plan feature
  ChooseTier = 'ChooseTier',
  ChooseTierPadletQuota = 'ChooseTierPadletQuota',
  ChooseTierFileSizeQuota = 'ChooseTierFileSizeQuota',
  ScheduleGold = 'ScheduleGold',
  SchedulePlatinum = 'SchedulePlatinum',
  EstimatePlatinumCost = 'EstimatePlatinumCost',
  ConfirmMonthlyPlatinum = 'ConfirmMonthlyPlatinum',
  ConfirmMonthlyPlatinumDifferentCurrency = 'ConfirmMonthlyPlatinumDifferentCurrency',
  ConfirmAnnualPlatinum = 'ConfirmAnnualPlatinum',
  ConfirmAnnualPlatinumDifferentCurrency = 'ConfirmAnnualPlatinumDifferentCurrency',
  // Switch from Legacy Platinum to Platinum
  EstimateUpgradePlatinumPlanCost = 'EstimateUpgradePlatinumPlanCost',
  ConfirmUpgradePlatinumPlanMonthly = 'ConfirmUpgradePlatinumPlanMonthly',
  ConfirmUpgradePlatinumPlanDifferentCurrencyMonthly = 'ConfirmUpgradePlatinumPlanDifferentCurrencyMonthly',
  ConfirmUpgradePlatinumPlanAnnual = 'ConfirmUpgradePlatinumPlanAnnual',
  ConfirmUpgradePlatinumPlanDifferentCurrencyAnnual = 'ConfirmUpgradePlatinumPlanDifferentCurrencyAnnual',
}

enum UpgradeSource {
  WallQuota = 'WallQuota',
  FileSizeLimit = 'FileSizeLimit',
  DashboardSettingsClick = 'DashboardSettingsClick',
  ProfileClick = 'ProfileClick',
  SurfaceProfileClick = 'SurfaceProfileClick',
  DashboardProfileClick = 'DashboardProfileClick',
  NewSignup = 'NewSignup',
}

interface UpgradeModalState {
  xUpgradeModal: boolean
  upgradeStep: UpgradeStep | null
  upgradeError: string
  upgradeSource: UpgradeSource | null
  upgradeStatusCheckUrl: string
  isProcessingUpgrade: boolean
  estimatedUpgradeCost: EstimateSwitchingCostResult | null
  hasScheduledChanges: boolean
  isLoadingScheduledChangesInformation: boolean
  hasUserTriggeredUpgrade: boolean
}

interface PlansState extends UpgradeModalState {
  plans: {
    platinum: { annual: {} | Plan; monthly: {} | Plan }
    gold: { annual: {} | Plan; monthly: {} | Plan }
  }
  advertisedQuotas: AdvertisedQuotasResult
}

const upgradeModalState: UpgradeModalState = {
  xUpgradeModal: false,
  upgradeStep: null,
  upgradeError: '',
  upgradeSource: null,
  upgradeStatusCheckUrl: '',
  isProcessingUpgrade: false,
  estimatedUpgradeCost: null,
  hasScheduledChanges: false,
  isLoadingScheduledChangesInformation: false,
  hasUserTriggeredUpgrade: false,
}

const blankState = (): PlansState => ({
  plans: {
    platinum: { annual: {}, monthly: {} },
    gold: { annual: {}, monthly: {} },
  },
  advertisedQuotas: {
    neon: {
      max_upload_megabytes: null,
      max_walls: null,
    },
    gold: {
      max_upload_megabytes: null,
      max_walls: null,
    },
    platinum: {
      max_upload_megabytes: null,
      max_walls: null,
    },
  },
  ...upgradeModalState,
})

const PlansModule: Module<PlansState, object> = {
  namespaced: true,
  state: blankState,
  getters: {
    xUpgradeModal: (state) => state.xUpgradeModal,
    upgradeStep: (state) => state.upgradeStep,
    upgradeSource: (state) => state.upgradeSource,
    plans: (state) => state.plans,
    plansFetched: (state) => Object.keys(state.plans.platinum.annual).length !== 0,
    isLoadingAdvertisedQuotas: (state) => !state.advertisedQuotas.platinum.max_walls,
    upgradeError: (state): string => state.upgradeError,
    isProcessingUpgrade: (state): boolean => state.isProcessingUpgrade,
    estimatedUpgradeCost: (state): EstimateSwitchingCostResult | null => state.estimatedUpgradeCost,
    hasScheduledChanges: (state): boolean => state.hasScheduledChanges,
    isLoadingScheduledChangesInformation: (state): boolean => state.isLoadingScheduledChangesInformation,
    hasUserTriggeredUpgrade: (state): boolean => state.hasUserTriggeredUpgrade,
    advertisedQuotas: (state) => state.advertisedQuotas,
  },
  mutations: {
    FETCHED_PLANS(state, plans: Plan[]) {
      // Convert a list to a hash that is easy to use in our component
      const hashedPlans = {
        platinum: { annual: {}, monthly: {} },
        gold: { annual: {}, monthly: {} },
      }
      for (const plan of plans) {
        plan.formattedPrice = translatePrice(parseFloat(plan.price), plan.currency)
        hashedPlans[plan.tier][plan.period] = plan
      }
      state.plans = hashedPlans
    },
    FETCHED_ADVERTISED_QUOTAS(state, advertisedQuotas) {
      state.advertisedQuotas = advertisedQuotas
    },
    SHOW_PLAN_UPGRADE_MODAL(state, step: UpgradeStep): void {
      state.xUpgradeModal = true
      state.upgradeStep = step
    },
    SET_UPGRADE_SOURCE(state, upgradeSource: UpgradeSource): void {
      state.upgradeSource = upgradeSource
    },
    CHANGE_STEP_PLAN_UPGRADE_MODAL(state, step: UpgradeStep): void {
      state.upgradeStep = step
    },
    CLOSE_PLAN_UPGRADE_MODAL(state): void {
      Object.assign(state, upgradeModalState)
    },
    SET_ESTIMATED_UPGRADE_COST(state, estimatedUpgradeCost: EstimateSwitchingCostResult): void {
      state.estimatedUpgradeCost = estimatedUpgradeCost
    },
    SET_UPGRADE_ERROR(state, upgradeError: string): void {
      state.upgradeError = upgradeError
    },
    SET_IS_PROCESSING_UPGRADE(state, isProcessingUpgrade: boolean): void {
      state.isProcessingUpgrade = isProcessingUpgrade
    },
    SET_HAS_SCHEDULED_CHANGES(state, hasScheduledChanges: boolean): void {
      state.hasScheduledChanges = hasScheduledChanges
    },
    START_LOADING_SCHEDULED_CHANGES_INFORMATION(state): void {
      state.isLoadingScheduledChangesInformation = true
    },
    DONE_LOADING_SCHEDULED_CHANGES_INFORMATION(state): void {
      state.isLoadingScheduledChangesInformation = false
    },
    SET_HAS_USER_TRIGGERED_UPGRADE(state, hasUserTriggeredUpgrade: boolean): void {
      state.hasUserTriggeredUpgrade = hasUserTriggeredUpgrade
    },
  },
  actions: {
    async fetchAdvertisedQuotas({ commit }): Promise<void> {
      const response = await PlanApi.fetchAdvertisedQuotas()
      commit('FETCHED_ADVERTISED_QUOTAS', response.data)
    },
    async fetchPlans({ commit }): Promise<void> {
      const response = await PlanApi.fetch()
      commit('FETCHED_PLANS', response.data)
    },
    setUpgradeSource({ commit }, { upgradeSource }: { upgradeSource: UpgradeSource }): void {
      commit('SET_UPGRADE_SOURCE', upgradeSource)
    },
    showPlanUpgradeModal(
      { commit },
      { step, upgradeSource }: { upgradeSource: UpgradeSource | null; step: UpgradeStep },
    ): void {
      if (upgradeSource) {
        commit('SET_UPGRADE_SOURCE', upgradeSource)
      }
      commit('SHOW_PLAN_UPGRADE_MODAL', step)
    },
    changeStepPlanUpgradeModal({ commit }, step: UpgradeStep): void {
      commit('CHANGE_STEP_PLAN_UPGRADE_MODAL', step)
    },
    closePlanUpgradeModal({ commit }): void {
      commit('CLOSE_PLAN_UPGRADE_MODAL')
    },
    showPlanUpgradeModalCheckingScheduledChanges(
      { dispatch },
      { step, membershipTier, paymentSchedule, upgradeSource },
    ): void {
      dispatch('showPlanUpgradeModal', { step, upgradeSource })
      if (membershipTier === 'gold') {
        dispatch('fetchScheduledChanges', { membershipTier, paymentSchedule })
      }
    },
    showPlanUpgradeToGoldModalCheckingQuota(
      { dispatch },
      { tierQuota, wallsUsed }: { tierQuota: number; wallsUsed: number },
    ): void {
      if (wallsUsed > tierQuota) {
        dispatch('showAboveQuotaOnUpgradeConfirmationDialog', {
          wallsUsed,
        })
        return
      }
      dispatch('showPlanUpgradeToGoldModal', { upgradeSource: UpgradeSource.DashboardSettingsClick })
    },
    userTriggeredUpgrade({ dispatch }, { user, upgradeSource }: { user: User; upgradeSource: UpgradeSource }): void {
      dispatch('showPlanUpgradeModalCheckingScheduledChanges', {
        step: UpgradeStep.ChooseTier,
        membershipTier: user.membership_tier,
        paymentSchedule: user.period,
        upgradeSource: upgradeSource,
      })
    },
    quotaTriggeredUpgrade({ dispatch }, { user }: { user: User }): void {
      dispatch('showPlanUpgradeModalCheckingScheduledChanges', {
        step: UpgradeStep.ChooseTierPadletQuota,
        membershipTier: user.membership_tier,
        paymentSchedule: user.period,
        upgradeSource: UpgradeSource.WallQuota,
      })
    },
    showAboveQuotaOnUpgradeConfirmationDialog(
      { rootGetters, getters, dispatch },
      { wallsUsed }: { wallsUsed: number },
    ): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...THINKING_FACE_ICON,
          layoutVariant: rootGetters['browser/isPhone']
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title: __('You’re above this tier’s padlet limit'),
          body: __(
            'You currently have %{amount} padlets, but our Gold tier only supports %{goldQuota}. You won’t be able to create new padlets until you are below the Gold tier limit. Consider switching to our Platinum tier instead.',
            { amount: wallsUsed, goldQuota: getters.advertisedQuotas.gold.max_walls },
          ),
          confirmButtonText: __('Continue with Gold'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [
            {
              type: 'plans/showPlanUpgradeToGoldModal',
              payload: { upgradeSource: UpgradeSource.DashboardSettingsClick },
            },
          ],
        },
        { root: true },
      )
    },
    showPlanUpgradeToGoldModal({ dispatch }, { upgradeSource = null }: { upgradeSource: UpgradeSource | null }): void {
      dispatch('showPlanUpgradeModal', { step: UpgradeStep.ScheduleGold, upgradeSource })
    },
    async fetchEstimatedUpgradeCost({ commit, dispatch, getters }, { paymentSchedule }): Promise<void> {
      const body = JSON.stringify({
        plan_id: getters.plans.platinum[normalizedPaymentSchedule(paymentSchedule)].id,
      })
      try {
        commit('SET_ESTIMATED_UPGRADE_COST', await BillingApi.estimateSwitchingCost({ body }))
      } catch (e) {
        switch (e.status) {
          case 422:
            commit(
              'SET_UPGRADE_ERROR',
              __(
                'Sorry, we are not able to process your request because your last invoice is unpaid. Please update your payment method and try again.',
              ),
            )
            break
          case 406:
            dispatch('closePlanUpgradeModal')
            dispatch('triggerSwitchingErrorBillingSettingsRedirect')
            break
          default:
            commit(
              'SET_UPGRADE_ERROR',
              __("We're sorry, we were unable to process your request. Please try again later."),
            )
        }
        e.status === 422
          ? commit(
              'SET_UPGRADE_ERROR',
              __(
                'Sorry, we are not able to process your request because your last invoice is unpaid. Please update your payment method and try again.',
              ),
            )
          : commit(
              'SET_UPGRADE_ERROR',
              __("We're sorry, we were unable to process your request. Please try again later."),
            )
      }
    },
    showUpgradeToPlatinumModal(
      { getters, dispatch },
      { membershipTier, paymentSchedule, userPlanCurrency, unusedPortion },
    ): void {
      if (membershipTier === 'neon') {
        dispatch('showPlanUpgradeModal', {
          step: UpgradeStep.SchedulePlatinum,
          upgradeSource: UpgradeSource.DashboardSettingsClick,
        })
      } else if (membershipTier === 'gold') {
        if (getters.plans.platinum.monthly.currency === userPlanCurrency) {
          dispatch('showUpgradeFromGoldToPlatinumModal', {
            paymentSchedule: paymentSchedule,
            upgradeSource: UpgradeSource.DashboardSettingsClick,
          })
        } else {
          dispatch('showUpgradeFromGoldToPlatinumDifferentCurrencyModal', {
            paymentSchedule: paymentSchedule,
            unusedPortion,
            upgradeSource: UpgradeSource.DashboardSettingsClick,
          })
        }
      } else {
        captureMessage('User tried to upgrade to Platinum while on Platinum.')
      }
    },
    showUpgradeToPlatinumModalCheckingScheduledChanges(
      { commit, getters, dispatch },
      { membershipTier, paymentSchedule },
    ): void {
      if (membershipTier === 'neon') {
        dispatch('showPlanUpgradeModal', { step: UpgradeStep.SchedulePlatinum })
        return
      }
      // If scheduled changes is still loading, we wait for that to finish first before `showUpgradeFromGoldToPlatinumModal`
      if (getters.isLoadingScheduledChangesInformation) {
        commit('SET_HAS_USER_TRIGGERED_UPGRADE', true)
        return
      }
      if (getters.hasScheduledChanges) {
        commit('CLOSE_PLAN_UPGRADE_MODAL')
        dispatch('triggerScheduledChangesConfirmationDialog')
        return
      }
      dispatch('changeStepUpgradeFromGoldToPlatinumModal', { paymentSchedule })
    },
    async showUpgradeFromGoldToPlatinumModal({ commit, dispatch }, { paymentSchedule, upgradeSource }): Promise<void> {
      dispatch('showPlanUpgradeModal', { step: UpgradeStep.EstimatePlatinumCost, upgradeSource })
      await dispatch('fetchEstimatedUpgradeCost', { paymentSchedule })
      commit(
        'CHANGE_STEP_PLAN_UPGRADE_MODAL',
        paymentSchedule === 'month' ? UpgradeStep.ConfirmMonthlyPlatinum : UpgradeStep.ConfirmAnnualPlatinum,
      )
    },
    showUpgradeFromGoldToPlatinumDifferentCurrencyModal(
      { commit, dispatch },
      { paymentSchedule, unusedPortion, upgradeSource },
    ): void {
      commit('SET_ESTIMATED_UPGRADE_COST', {
        is_refund: null,
        currency_code: null,
        new_plan_cost: null,
        refund_amount: null,
        amount_due: null,
        invoice_amount: null,
        unused_portion: unusedPortion,
      })
      dispatch('showPlanUpgradeModal', {
        step:
          paymentSchedule === 'month'
            ? UpgradeStep.ConfirmMonthlyPlatinumDifferentCurrency
            : UpgradeStep.ConfirmAnnualPlatinumDifferentCurrency,
        upgradeSource,
      })
    },
    async showUpgradePlatinumPlanModal(
      { commit, getters, dispatch },
      { paymentSchedule, userPlanCurrency, unusedPortion },
    ): Promise<void> {
      if (getters.plans.platinum.monthly.currency === userPlanCurrency) {
        dispatch('showPlanUpgradeModal', {
          step: UpgradeStep.EstimateUpgradePlatinumPlanCost,
          upgradeSouce: UpgradeSource.DashboardSettingsClick,
        })
        await dispatch('fetchEstimatedUpgradeCost', { paymentSchedule })
        commit(
          'CHANGE_STEP_PLAN_UPGRADE_MODAL',
          paymentSchedule === 'month'
            ? UpgradeStep.ConfirmUpgradePlatinumPlanMonthly
            : UpgradeStep.ConfirmUpgradePlatinumPlanAnnual,
        )
      } else {
        commit('SET_ESTIMATED_UPGRADE_COST', {
          is_refund: null,
          currency_code: null,
          new_plan_cost: null,
          refund_amount: null,
          amount_due: null,
          invoice_amount: null,
          unused_portion: unusedPortion,
        })
        dispatch('showPlanUpgradeModal', {
          step:
            paymentSchedule === 'month'
              ? UpgradeStep.ConfirmUpgradePlatinumPlanDifferentCurrencyMonthly
              : UpgradeStep.ConfirmUpgradePlatinumPlanDifferentCurrencyAnnual,
          upgradeSource: UpgradeSource.DashboardSettingsClick,
        })
      }
    },
    async changeStepUpgradeFromGoldToPlatinumModal({ commit, dispatch }, { paymentSchedule }): Promise<void> {
      commit('CHANGE_STEP_PLAN_UPGRADE_MODAL', UpgradeStep.EstimatePlatinumCost)
      await dispatch('fetchEstimatedUpgradeCost', { paymentSchedule })
      commit(
        'CHANGE_STEP_PLAN_UPGRADE_MODAL',
        paymentSchedule === 'month' ? UpgradeStep.ConfirmMonthlyPlatinum : UpgradeStep.ConfirmAnnualPlatinum,
      )
    },
    async upgradeToPlatinum({ getters, commit, dispatch }, { paymentSchedule }): Promise<void> {
      try {
        commit('SET_IS_PROCESSING_UPGRADE', true)
        const body = JSON.stringify({
          switch_type: SwitchType.SwitchImmediately,
          plan_id: getters.plans.platinum[normalizedPaymentSchedule(paymentSchedule)].id,
        })
        const {
          links: { status },
        } = await BillingApi.switchPlan({ body })
        const response = await poll({
          pollingUrl: status,
          validationCallback: (response) => response.hasOwnProperty('switch_status'),
          options: { intervalSecs: 5, maxAttempts: 10 },
        })
        if (response.switch_status.hasOwnProperty('error_message')) {
          commit('SET_UPGRADE_ERROR', response.switch_status.error_message)
        } else if (!response.switch_status.is_switched) {
          commit(
            'SET_UPGRADE_ERROR',
            __(`We're sorry, we were unable to process your request. Please try again later.`),
          )
        } else if (response.switch_status.hasOwnProperty('is_refunded') && !response.switch_status.is_refunded) {
          dispatch('closePlanUpgradeModal')
          dispatch(
            'alertDialog/openAlertDialog',
            {
              ...ALERT_ICON,
              title: __('Refund failed!'),
              body: __(
                'You have successfully switched plans! Unfortunately, your bank is unable to process your refund. We have extended your term end for now.',
              ),
              closeButtonText: __('Okay'),
              afterCloseActions: [{ type: 'reloadPage' }],
            },
            { root: true },
          )
        } else {
          // Redirect the user to /memberships/confirmation and add the "next" query param
          // containing the current URL.
          navigateTo(transformCurrentUrl({}, { path: '/memberships/confirmation', search: { next: currentUrl() } }))
        }
      } catch (e) {
        commit('SET_UPGRADE_ERROR', __(`We're sorry, we were unable to process your request. Please try again later.`))
      } finally {
        commit('SET_IS_PROCESSING_UPGRADE', false)
      }
    },
    async upgradeToPlatinumDifferentCurrency({ getters, commit, dispatch }, { paymentSchedule }): Promise<void> {
      try {
        commit('SET_IS_PROCESSING_UPGRADE', true)
        const body = JSON.stringify({
          plan_id: getters.plans.platinum[normalizedPaymentSchedule(paymentSchedule)].id,
        })
        await BillingApi.switchCurrency({ body })
        // Redirect the user to /memberships/confirmation and add the "next" query param
        // containing the current URL.
        navigateTo(transformCurrentUrl({}, { path: '/memberships/confirmation', search: { next: currentUrl() } }))
      } catch (e) {
        try {
          if (JSON.parse(e.message).error === 'refund_failed') {
            dispatch('closePlanUpgradeModal')
            dispatch(
              'alertDialog/openAlertDialog',
              {
                ...ALERT_ICON,
                title: __('Refund failed!'),
                body: __(
                  'Sorry, we were unable to refund your previous subscription through your card. We have extended your current subscription.',
                ),
                closeButtonText: __('Okay'),
                afterCloseActions: [{ type: 'plans/reloadPage' }],
              },
              { root: true },
            )
          } else {
            commit('SET_UPGRADE_ERROR', JSON.parse(e.message).error)
          }
        } catch {
          commit(
            'SET_UPGRADE_ERROR',
            __(`We're sorry, we were unable to process your request. Please try again later.`),
          )
        }
      } finally {
        commit('SET_IS_PROCESSING_UPGRADE', false)
      }
    },
    async fetchScheduledChanges({ commit, dispatch }, { paymentSchedule }): Promise<void> {
      commit('START_LOADING_SCHEDULED_CHANGES_INFORMATION')
      try {
        const response = await BillingApi.fetchScheduledChanges()
        commit('SET_HAS_SCHEDULED_CHANGES', response.data.has_scheduled_changes)
        dispatch('handleScheduledChangesLoaded', { paymentSchedule })
      } finally {
        commit('DONE_LOADING_SCHEDULED_CHANGES_INFORMATION')
      }
    },
    handleScheduledChangesLoaded({ commit, getters, dispatch }, { paymentSchedule }): void {
      // If the user already triggered an upgrade from Gold to Platinum and is waiting for loading schedule changes to finish
      if (getters.hasUserTriggeredUpgrade) {
        commit('SET_HAS_USER_TRIGGERED_UPGRADE', false)
        if (getters.hasScheduledChanges) {
          commit('CLOSE_PLAN_UPGRADE_MODAL')
          dispatch('triggerScheduledChangesConfirmationDialog')
        } else {
          dispatch('changeStepUpgradeFromGoldToPlatinumModal', { paymentSchedule })
        }
      }
    },
    triggerScheduledChangesConfirmationDialog({ dispatch, rootGetters }): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...COLD_FACE_EMOJI,
          layoutVariant: rootGetters['browser/isPhone']
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title: __('Upgrade currently unavailable'),
          body: __(
            'We are unable to upgrade your membership due to changes that are pending in your account. You can check your membership status and make changes from the billing page in settings.',
          ),
          confirmButtonText: __('Go to billing'),
          cancelButtonText: __('Cancel'),
          afterConfirmActions: [{ type: 'plans/navigateToBillingSettingsPage' }],
          shouldFadeIn: false,
        },
        { root: true },
      )
    },
    triggerSwitchingErrorBillingSettingsRedirect({ dispatch, rootGetters }): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...FACE_WITH_ROLLING_EYES_EMOJI,
          layoutVariant: rootGetters['browser/isPhone']
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title: __('Upgrade currently unavailable'),
          body: __('We are unable to process this action from this page. Please visit the billing page and try again'),
          confirmButtonText: __('Go to billing'),
          cancelButtonText: __('Cancel'),
          afterConfirmActions: [{ type: 'plans/navigateToBillingSettingsPage' }],
          shouldFadeIn: false,
        },
        { root: true },
      )
    },
    navigateToBillingSettingsPage(): void {
      navigateTo('/dashboard/settings/billing')
    },
    reloadPage(): void {
      reload()
    },
  },
}

export default PlansModule
export { UpgradeSource, UpgradeStep }
